// MobileNav Component - Bottom navigation for mobile devices
// Provides easy access to main sections on small screens
// Fixed at bottom with touch-friendly tap targets

import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useEntitlements } from '../hooks/useEntitlements';

interface NavItem {
  path: string;
  label: string;
  icon: JSX.Element;
  requiresAuth?: boolean;
  requiresAdmin?: boolean;
}

export function MobileNav() {
  const location = useLocation();
  const { user, isEmailVerified, profileCompleted } = useAuth();
  const { hasEntitlement } = useEntitlements();

  // Authenticated is not enough. Every destination below is a `VerifiedRoute`,
  // which is `requireEmailVerified && requireProfileComplete` — so for a user
  // missing either one, all three links bounce straight back to the page they
  // were trying to leave. That is the loop a stranded signup walks: Home,
  // Profile and Settings each returning them to /verify-email, on the one page
  // that had no sign-out.
  //
  // `profileCompleted` matters as much as verification and is easy to miss:
  // /complete-profile is itself behind `requireEmailVerified`, so anyone parked
  // there is already verified and a verification-only gate leaves them looping
  // exactly the same way, one step later.
  //
  // Hide the nav until it can actually navigate somewhere.
  if (!user || !isEmailVerified || !profileCompleted) return null;

  const navItems: NavItem[] = [
    {
      path: '/dashboard',
      label: 'Home',
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
          />
        </svg>
      ),
    },
    {
      path: '/profile',
      label: 'Profile',
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
      ),
    },
    {
      path: '/settings',
      label: 'Settings',
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      ),
    },
  ];

  // Add admin item if user has sys:admin entitlement
  if (hasEntitlement('sys:admin')) {
    navItems.push({
      path: '/admin',
      label: 'Admin',
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
          />
        </svg>
      ),
      requiresAdmin: true,
    });
  }

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 z-50">
      <div className="grid grid-cols-4 h-16">
        {navItems.map(item => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center space-y-1 transition-colors ${
                isActive
                  ? 'text-sky-600 dark:text-sky-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {item.icon}
              <span className="text-xs font-medium">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
